// The mark of a caster, beside its name: its pawn, whose head is its sign.
// `casterPawnShapes.ts` has the shapes. The colors come from
// `--caster-primary` and `--caster-secondary`, which `caster.css` sets on
// anything with `data-caster`. Until 2026-10-15 the mark was a bishop, and a
// plate of the `pick` variant had the square symbol of `casterMarkShapes.ts`.

import type { CasterId } from '../content/casters';
import { svg } from '../lib/svg';
import { casterPawnMarkParts } from './casterPawn';
import { GLYPH_BOX } from './PieceGlyph';

/** A square of the glyph box that holds every pawn of a caster, with its head. */
const PAWN_MARK_VIEW = '14 16 72 72';

/** The caster as its pawn, in a square, for the caster select. */
export const createCasterPawn = (id: CasterId): SVGSVGElement =>
  svg(
    'svg',
    { class: 'caster-mark caster-mark--pawn', viewBox: PAWN_MARK_VIEW, 'aria-hidden': 'true' },
    casterPawnMarkParts(id, GLYPH_BOX),
  );

/** The box of the small pawn: the bounds of every pawn of a caster, and no more. */
export const SMALL_PAWN_VIEW = { x: 26, y: 17, width: 48, height: 70 } as const;

/** The parts of the small pawn in glyph units. A badge on the board puts them in its own group. */
export const createCasterPawnSmallParts = (id: CasterId): SVGElement[] =>
  casterPawnMarkParts(id, GLYPH_BOX);

/**
 * The small pawn, for a mark of 24px or less: a plate in a match and the line
 * of Moon on the phone. Its view is cropped to the pawn, so the pawn takes the
 * full height of the mark.
 */
export const createCasterPawnSmall = (id: CasterId): SVGSVGElement => {
  const { x, y, width, height } = SMALL_PAWN_VIEW;
  return svg(
    'svg',
    {
      class: 'caster-mark caster-mark--small-pawn',
      viewBox: `${x} ${y} ${width} ${height}`,
      'aria-hidden': 'true',
    },
    createCasterPawnSmallParts(id),
  );
};
